// =============================================================================
// Menu domain helpers
// =============================================================================

export const MENU_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
export const MENU_IMAGE_ACCEPT = "image/jpeg,image/png,image/webp,image/gif";

export type MenuTag = "popular" | "spicy" | "vegetarian" | "new";
export type ModifierSelectionType = "single" | "multiple";
export type PromotionKind = "item_discount" | "storefront_banner";
export type DiscountType = "percentage" | "fixed";
export type PromotionWindow = "active" | "scheduled" | "expired";

export const MENU_TAGS: MenuTag[] = ["popular", "spicy", "vegetarian", "new"];

export interface MenuCategoryRecord {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  image_url: string | null;
  sort_order: number;
  is_available: boolean;
  created_at: string;
}

export interface MenuItemRecord {
  id: string;
  tenant_id: string;
  category_id: string;
  name: string;
  description: string | null;
  image_url: string | null;
  base_price: number;
  currency: string;
  is_available: boolean;
  is_vegetarian: boolean;
  is_vegan: boolean;
  is_gluten_free: boolean;
  allergens: string[];
  prep_time_minutes: number;
  sort_order: number;
  tags: MenuTag[];
  is_featured: boolean;
  created_at: string;
}

export interface MenuModifierOptionRecord {
  id: string;
  group_id: string;
  name: string;
  price_delta: number;
  is_available: boolean;
  sort_order: number;
  created_at: string;
}

export interface MenuModifierGroupRecord {
  id: string;
  tenant_id: string;
  item_id: string;
  name: string;
  selection_type: ModifierSelectionType;
  is_required: boolean;
  sort_order: number;
  created_at: string;
  options?: MenuModifierOptionRecord[];
}

export interface MenuPromotionRecord {
  id: string;
  tenant_id: string;
  kind: PromotionKind;
  title: string;
  description: string | null;
  image_url: string | null;
  discount_type: DiscountType | null;
  discount_value: number | null;
  target_item_id: string | null;
  target_category_id: string | null;
  starts_at: string | null;
  ends_at: string | null;
  is_active: boolean;
  created_at: string;
}

export interface MenuItemWithModifiers extends MenuItemRecord {
  modifier_groups: MenuModifierGroupRecord[];
}

export interface PublicMenuPayload {
  tenant: { id: string; name: string; slug: string; currency: string; locale?: string; public_store_enabled?: boolean; logo_url?: string | null; cover_url?: string | null } | null;
  categories: Array<{
    id: string;
    name: string;
    description: string | null;
    image_url: string | null;
    sort_order: number;
  }>;
  items: Array<{
    id: string;
    category_id: string;
    name: string;
    description: string | null;
    image_url: string | null;
    base_price: number;
    currency: string;
    tags: MenuTag[];
    is_featured: boolean;
    /** When false, storefront shows grayed card and blocks add-to-cart. Defaults true if omitted. */
    is_available?: boolean;
    sort_order: number;
    modifier_groups: Array<{
      id: string;
      name: string;
      selection_type: ModifierSelectionType;
      is_required: boolean;
      sort_order: number;
      options: Array<{ id: string; name: string; price_delta: number; sort_order: number }>;
    }>;
  }>;
  promotions: Array<{
    id: string;
    kind: PromotionKind;
    title: string;
    description: string | null;
    image_url: string | null;
    discount_type: DiscountType | null;
    discount_value: number | null;
    target_item_id: string | null;
    target_category_id: string | null;
    starts_at: string | null;
    ends_at: string | null;
  }>;
}

export interface ModifierGroupDraft {
  id?: string;
  name: string;
  selection_type: ModifierSelectionType;
  is_required: boolean;
  sort_order: number;
  options: ModifierOptionDraft[];
}

export interface ModifierOptionDraft {
  id?: string;
  name: string;
  price_delta: number;
  is_available: boolean;
  sort_order: number;
}

export function normalizeMenuTags(tags: string[] | null | undefined): MenuTag[] {
  if (!tags?.length) return [];
  return tags.filter((t): t is MenuTag => MENU_TAGS.includes(t as MenuTag));
}

export function getPromotionWindow(
  promo: Pick<MenuPromotionRecord, "starts_at" | "ends_at" | "is_active">,
  now = new Date()
): PromotionWindow {
  if (!promo.is_active) return "expired";
  const start = promo.starts_at ? new Date(promo.starts_at) : null;
  const end = promo.ends_at ? new Date(promo.ends_at) : null;
  if (end && end < now) return "expired";
  if (start && start > now) return "scheduled";
  return "active";
}

export function validateMenuItemForm(input: {
  name: string;
  base_price: number;
  category_id: string;
}): string | null {
  if (!input.name.trim()) return "nameRequired";
  if (!input.category_id) return "categoryRequired";
  if (!Number.isFinite(input.base_price) || input.base_price <= 0) return "priceInvalid";
  return null;
}

export function validateModifierGroups(groups: ModifierGroupDraft[]): string | null {
  for (const group of groups) {
    if (!group.name.trim()) return "modifierGroupNameRequired";
    const availableOptions = group.options.filter((o) => o.name.trim());
    if (group.is_required && group.selection_type === "single" && availableOptions.length === 0) {
      return "modifierGroupOptionsRequired";
    }
  }
  return null;
}

export function validatePromotionDates(startsAt: string | null, endsAt: string | null): string | null {
  if (!startsAt || !endsAt) return null;
  if (new Date(endsAt) < new Date(startsAt)) return "promoDateInvalid";
  return null;
}

export function validateMenuImageFile(file: File): string | null {
  if (!file.type.startsWith("image/")) return "imageTypeInvalid";
  if (file.size > MENU_IMAGE_MAX_BYTES) return "imageTooLarge";
  return null;
}

export function getStorefrontUrl(slug: string, baseUrl?: string): string {
  const origin = baseUrl || (typeof window !== "undefined" ? window.location.origin : "");
  const storefrontPort = origin.includes(":3002") ? origin.replace(":3002", ":3003") : origin;
  return `${storefrontPort.replace(/\/$/, "")}/${slug}`;
}

export function formatDiscountLabel(
  discountType: DiscountType | null,
  discountValue: number | null
): string | null {
  if (!discountType || discountValue == null) return null;
  if (discountType === "percentage") return `${discountValue}% off`;
  return `$${discountValue} off`;
}

export function reorderById<T extends { id: string; sort_order: number }>(
  items: T[],
  draggedId: string,
  targetId: string
): T[] {
  const from = items.findIndex((i) => i.id === draggedId);
  const to = items.findIndex((i) => i.id === targetId);
  if (from < 0 || to < 0 || from === to) return items;
  const next = [...items];
  const [moved] = next.splice(from, 1);
  if (!moved) return items;
  next.splice(to, 0, moved);
  return next.map((item, index) => ({ ...item, sort_order: index }));
}
